// src/pages/Admin.tsx
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTable } from '@refinedev/antd';
import { useUpdate, useDelete, useGetIdentity } from '@refinedev/core';
import { Card, Table, Tag, Button, Statistic, Row, Col, Select, Modal, Form, InputNumber, message, Space, Input, Tabs, Tooltip } from 'antd';
import { UserOutlined, VideoCameraOutlined, FileTextOutlined, DollarOutlined, EditOutlined, DeleteOutlined, SearchOutlined, TeamOutlined, PlusOutlined, CopyOutlined, StopOutlined } from '@ant-design/icons';
import { increment } from 'firebase/firestore';
import { auth } from '../utils/firebase';

const { Option } = Select;
const { Search } = Input;

interface User {
  id: string;
  name: string;
  email: string;
  planType: string;
  planStatus: string;
  createdAt: any;
  usage: {
    videosThisMonth: number;
    scriptsThisMonth: number;
    videosAllTime: number;
    scriptsAllTime: number;
  };
}

interface Partner {
  id: string;
  slug: string;
  name: string;
  email: string;
  address: string;
  tax_id: string;
  commission_type: 'percentage' | 'flat_payment' | 'flat_signup';
  commission_value: number;
  is_active: boolean;
  total_link_views: number;
  unique_link_views: number;
  total_signups: number;
  total_revenue: number;
  total_commission_owed: number;
  created_at: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const AIExpertTab: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPrompt = useCallback(async () => {
    setLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_URL}/global-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setPrompt(json.data?.ai_expert_prompt || '');
    } catch (e) {
      message.error('Failed to load AI Expert settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPrompt(); }, [fetchPrompt]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_URL}/global-settings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ai_expert_prompt: prompt }),
      });
      const json = await res.json();
      if (!res.ok) { message.error(json.error || 'Failed to save'); return; }
      message.success('AI Expert guidance saved');
    } catch (e) {
      message.error('Failed to save AI Expert settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="AI Expert Guidance" loading={loading}>
      <p style={{ color: '#6b7280', marginBottom: 12, maxWidth: 720 }}>
        This guidance is added to the AI Expert's instructions for every report chat.
        Use it to steer tone and to restrict what the assistant should or shouldn't say
        (e.g. avoid specific cost figures, always recommend a licensed pro, don't disparage
        the inspector). A built-in security layer — prompt-injection protection, report-only
        scope, and no disclosure of system instructions — is always applied and cannot be
        overridden by this text or by users.
      </p>
      <Input.TextArea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder={'e.g. Never give exact repair dollar amounts. Always recommend consulting a licensed contractor for scope and pricing. Keep a neutral, professional tone.'}
        autoSize={{ minRows: 8, maxRows: 20 }}
        maxLength={5000}
        showCount
      />
      <div style={{ marginTop: 16 }}>
        <Button type="primary" onClick={handleSave} loading={saving}>
          Save Guidance
        </Button>
      </div>
    </Card>
  );
};

const PartnersTab: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [form] = Form.useForm();

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_URL}/admin/partners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.data?.partners) setPartners(json.data.partners);
    } catch (e) {
      message.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const handleSave = async (values: any) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const url = editingPartner
        ? `${API_URL}/admin/partners/${editingPartner.id}`
        : `${API_URL}/admin/partners`;
      const res = await fetch(url, {
        method: editingPartner ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      const json = await res.json();
      if (!res.ok) { message.error(json.error || 'Failed to save partner'); return; }
      message.success(editingPartner ? 'Partner updated' : 'Partner created');
      setModalVisible(false);
      setEditingPartner(null);
      form.resetFields();
      fetchPartners();
    } catch (e) {
      message.error('Failed to save partner');
    }
  };

  const handleDeactivate = async (partner: Partner) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch(`${API_URL}/admin/partners/${partner.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success('Partner deactivated');
      fetchPartners();
    } catch (e) {
      message.error('Failed to deactivate partner');
    }
  };

  const handleReactivate = async (partner: Partner) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch(`${API_URL}/admin/partners/${partner.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: true }),
      });
      message.success('Partner reactivated');
      fetchPartners();
    } catch (e) {
      message.error('Failed to reactivate partner');
    }
  };

  const openEdit = (partner: Partner) => {
    setEditingPartner(partner);
    form.setFieldsValue(partner);
    setModalVisible(true);
  };

  const openCreate = () => {
    setEditingPartner(null);
    form.resetFields();
    form.setFieldsValue({ commission_type: 'percentage', commission_value: 20 });
    setModalVisible(true);
  };

  const copyLink = (slug: string) => {
    navigator.clipboard.writeText(`https://spectreviews.com/p/${slug}`);
    message.success('Partner link copied');
  };

  const totals = useMemo(() => partners.reduce((acc, p) => ({
    signups: acc.signups + (p.total_signups || 0),
    revenue: acc.revenue + (p.total_revenue || 0),
    commission: acc.commission + (p.total_commission_owed || 0),
  }), { signups: 0, revenue: 0, commission: 0 }), [partners]);

  const partnerColumns = [
    {
      title: 'Partner',
      key: 'name',
      render: (r: Partner) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.name}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{r.email}</div>
        </div>
      ),
    },
    {
      title: 'Link',
      key: 'slug',
      render: (r: Partner) => (
        <Tooltip title={`spectreviews.com/p/${r.slug}`}>
          <Button size="small" icon={<CopyOutlined />} onClick={() => copyLink(r.slug)}>
            /p/{r.slug}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Commission',
      key: 'commission',
      render: (r: Partner) => r.commission_type === 'percentage'
        ? `${r.commission_value}%`
        : r.commission_type === 'flat_signup'
        ? `$${r.commission_value}/signup`
        : `$${r.commission_value}/payment`,
    },
    {
      title: 'Views',
      key: 'views',
      render: (r: Partner) => (
        <Tooltip title={`${r.total_link_views || 0} total / ${r.unique_link_views || 0} unique`}>
          <span>{r.unique_link_views || 0} / {r.total_link_views || 0}</span>
        </Tooltip>
      ),
    },
    { title: 'Signups', dataIndex: 'total_signups', key: 'signups' },
    {
      title: 'Revenue',
      dataIndex: 'total_revenue',
      key: 'revenue',
      render: (v: number) => `$${(v || 0).toFixed(2)}`,
    },
    {
      title: 'Owed',
      dataIndex: 'total_commission_owed',
      key: 'owed',
      render: (v: number) => `$${(v || 0).toFixed(2)}`,
    },
    {
      title: 'Status',
      key: 'status',
      render: (r: Partner) => (
        <Tag color={r.is_active ? 'green' : 'red'}>{r.is_active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (r: Partner) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
          {r.is_active ? (
            <Button size="small" danger icon={<StopOutlined />} onClick={() => handleDeactivate(r)}>Deactivate</Button>
          ) : (
            <Button size="small" onClick={() => handleReactivate(r)}>Reactivate</Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card><Statistic title="Total Partners" value={partners.length} prefix={<TeamOutlined />} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Total Signups" value={totals.signups} prefix={<UserOutlined />} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Revenue Generated" value={totals.revenue} prefix={<DollarOutlined />} precision={2} suffix="USD" /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Commission Owed" value={totals.commission} prefix={<DollarOutlined />} precision={2} suffix="USD" /></Card>
        </Col>
      </Row>

      <Card
        title="Partner Management"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Partner</Button>}
      >
        <Table dataSource={partners} columns={partnerColumns} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} />
      </Card>

      <Modal
        title={editingPartner ? 'Edit Partner' : 'Add Partner'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditingPartner(null); form.resetFields(); }}
        footer={null}
        width={500}
      >
        <Form form={form} onFinish={handleSave} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          {!editingPartner && (
            <Form.Item name="slug" label="Slug (URL path)" rules={[{ required: true }, { pattern: /^[a-z0-9-]+$/, message: 'Lowercase letters, numbers, and hyphens only' }]}>
              <Input addonBefore="spectreviews.com/p/" />
            </Form.Item>
          )}
          <Form.Item name="email" label="Email">
            <Input />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="tax_id" label="Tax ID">
            <Input />
          </Form.Item>
          <Form.Item name="commission_type" label="Commission Type" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="percentage">Percentage of subscription</Select.Option>
              <Select.Option value="flat_payment">Flat amount per payment</Select.Option>
              <Select.Option value="flat_signup">Flat amount per signup</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="commission_value" label="Commission Value" rules={[{ required: true }]}>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              formatter={(value) => {
                const ct = form.getFieldValue('commission_type');
                if (ct === 'percentage') return `${value}%`;
                return `$ ${value}`;
              }}
              parser={(value) => {
                return Number((value || '').replace(/[$%\s]/g, '')) as any;
              }}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {editingPartner ? 'Update Partner' : 'Create Partner'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const Admin: React.FC = () => {
  const { data: currentUser } = useGetIdentity<any>();
  const { tableProps } = useTable<User>({ resource: 'users' });
  const { mutate: update } = useUpdate();
  const { mutate: deleteUser } = useDelete();
  
  const [creditModalVisible, setCreditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [creditForm] = Form.useForm();
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [impersonateUid, setImpersonateUid] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  // Filter users based on search text
  const filteredUsers = useMemo(() => {
    if (!searchText) return tableProps.dataSource;
    
    const lowercasedSearch = searchText.toLowerCase();
    return tableProps.dataSource?.filter((user) => {
      const nameMatch = user.name?.toLowerCase().includes(lowercasedSearch);
      const emailMatch = user.email?.toLowerCase().includes(lowercasedSearch);
      return nameMatch || emailMatch;
    });
  }, [tableProps.dataSource, searchText]);

  // Stats calculation - now using filtered users
  const stats = useMemo(() => {
    const users = searchText ? tableProps.dataSource : filteredUsers; // Use all users for stats regardless of search
    return users?.reduce((acc, user) => ({
      totalUsers: acc.totalUsers + 1,
      totalVideos: acc.totalVideos + (user.usage?.videosAllTime || 0),
      totalScripts: acc.totalScripts + (user.usage?.scriptsAllTime || 0),
      // Base MRR only (Agent $25 / Team $149). Overage revenue is billed by
      // Stripe and not reflected here.
      monthlyRevenue: acc.monthlyRevenue + (user.planType === 'standard' ? 25 : user.planType === 'pro' ? 149 : 0)
    }), { totalUsers: 0, totalVideos: 0, totalScripts: 0, monthlyRevenue: 0 }) || {
      totalUsers: 0, totalVideos: 0, totalScripts: 0, monthlyRevenue: 0
    };
  }, [tableProps.dataSource, filteredUsers, searchText]);

  if (!currentUser?.isAdmin) {
    return <div>Access Denied</div>;
  }

  const handlePlanChange = (userId: string, newPlan: string) => {
    update({
      resource: 'users',
      id: userId,
      values: { planType: newPlan }
    });
  };

  const handleAddCredits = (values: any) => {
    if (!selectedUser) return;
    
    update({
      resource: 'users',
      id: selectedUser.id,
      values: {
        'usage.videosThisMonth': increment(-values.extraVideos || 0),  // Negative increment to add credits by reducing usage
        'usage.scriptsThisMonth': increment(-values.extraScripts || 0)
      }
    });
    
    message.success('Credits added successfully');
    setCreditModalVisible(false);
    creditForm.resetFields();
  };

  const showCreditModal = (user: User) => {
    setSelectedUser(user);
    setCreditModalVisible(true);
    creditForm.setFieldsValue({
      videosThisMonth: user.usage?.videosThisMonth || 0,
      scriptsThisMonth: user.usage?.scriptsThisMonth || 0,
      extraVideos: 0,
      extraScripts: 0
    });
  };

  const showDeleteConfirm = (user: User) => {
    setSelectedUser(user);
    setDeleteConfirmVisible(true);
  };

  const handleDelete = () => {
    if (!selectedUser) return;
    
    deleteUser({
      resource: 'users',
      id: selectedUser.id
    });
    
    message.success('User deleted successfully');
    setDeleteConfirmVisible(false);
  };

  const handleImpersonate = (userId: string) => {
    localStorage.setItem('impersonateUid', userId);
    message.success('Impersonation mode activated. Redirecting to videos...');
    // Redirect to videos page since non-admin users can't access admin page
    window.location.href = '/videos';
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const handleClearSearch = () => {
    setSearchText('');
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (record: User) => (
        <div>
          <div className="font-medium">{record.name}</div>
          <div className="text-sm text-gray-500">{record.email}</div>
        </div>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'planType',
      key: 'plan',
      render: (plan: string, record: User) => (
        <Select
          value={plan}
          onChange={(value) => handlePlanChange(record.id, value)}
          style={{ width: 120 }}
          size="small"
        >
          <Option value="free">Free</Option>
          <Option value="standard">Standard</Option>
          <Option value="pro">Pro</Option>
        </Select>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'planStatus',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {(status || 'UNKNOWN').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'This Month',
      key: 'thisMonth',
      render: (record: User) => (
        <div>
          <div>Videos: {record.usage?.videosThisMonth || 0}</div>
          <div>Scripts: {record.usage?.scriptsThisMonth || 0}</div>
        </div>
      ),
    },
    {
      title: 'All Time',
      key: 'allTime',
      render: (record: User) => (
        <div>
          <div>Videos: {record.usage?.videosAllTime || 0}</div>
          <div>Scripts: {record.usage?.scriptsAllTime || 0}</div>
        </div>
      ),
    },
    {
      title: 'Member Since',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: any) => date ? new Date(date.seconds * 1000).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: User) => (
        <Space wrap>
          <Button 
            icon={<EditOutlined />} 
            size="small" 
            onClick={() => showCreditModal(record)}
          >
            Add Credits
          </Button>
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            size="small" 
            onClick={() => showDeleteConfirm(record)}
          >
            Delete
          </Button>
          <Button 
            type="primary" 
            size="small" 
            onClick={() => handleImpersonate(record.id)}
          >
            Impersonate
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Tabs defaultActiveKey="users" items={[
        { key: 'users', label: 'Users', children: (
          <>
      {/* Stats Overview */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={stats.totalUsers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Videos Generated"
              value={stats.totalVideos}
              prefix={<VideoCameraOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Scripts Generated"
              value={stats.totalScripts}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Monthly Revenue"
              value={stats.monthlyRevenue}
              prefix={<DollarOutlined />}
              suffix="USD"
            />
          </Card>
        </Col>
      </Row>

      {/* User Management */}
      <Card 
        title="User Management"
        extra={
          <Search
            placeholder="Search by name or email"
            allowClear
            enterButton={<SearchOutlined />}
            size="middle"
            onSearch={handleSearch}
            onChange={(e) => handleSearch(e.target.value)}
            value={searchText}
            style={{ width: 300 }}
          />
        }
      >
        {searchText && (
          <div className="mb-4">
            <Space>
              <span>Found {filteredUsers?.length || 0} users matching "{searchText}"</span>
              <Button size="small" onClick={handleClearSearch}>Clear Search</Button>
            </Space>
          </div>
        )}
        <Table 
          {...tableProps}
          dataSource={filteredUsers}
          columns={columns} 
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} users`,
          }}
        />
      </Card>

      {/* Add Credits Modal */}
      <Modal
        title={`Add Credits for ${selectedUser?.name}`}
        open={creditModalVisible}
        onCancel={() => setCreditModalVisible(false)}
        footer={null}
      >
        <Form form={creditForm} onFinish={handleAddCredits} layout="vertical">
          <Form.Item
            name="extraVideos"
            label="Extra Videos This Month"
            initialValue={0}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="extraScripts"
            label="Extra Scripts This Month"
            initialValue={0}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Add Credits
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        title="Confirm Delete User"
        open={deleteConfirmVisible}
        onOk={handleDelete}
        onCancel={() => setDeleteConfirmVisible(false)}
        okText="Delete"
        okType="danger"
      >
        <p>Are you sure you want to delete user {selectedUser?.name} ({selectedUser?.email})?</p>
        <p>This action cannot be undone.</p>
      </Modal>
          </>
        )},
        { key: 'partners', label: 'Partners', children: <PartnersTab /> },
        { key: 'ai_expert', label: 'AI Expert', children: <AIExpertTab /> },
      ]} />
    </div>
  );
};

export default Admin;